import React, { useState } from "react";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import type { All8PageContent } from "@apa/core-renderers";
import type { ArrayFieldConfig, Path } from "../../Pages/pageComponentLibrary";
import { reorderList } from "../LayoutEditor/utils";
import { ParagraphRichEditor } from "./ParagraphRichEditor";
import { ParagraphRichEditorModal } from "./ParagraphRichEditorModal";
import { ParagraphBulkEditorModal } from "./ParagraphBulkEditorModal";
import { sanitizeParagraphHtml } from "./paragraphHtml";

function stripHtml(html: string): string {
  if (typeof html !== "string") return "";
  return html.replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]+>/g, "").trim();
}

function summary(item: string, index: number, itemLabel: string): string {
  const s = stripHtml(item);
  return s.slice(0, 60) + (s.length > 60 ? "…" : "") || `${itemLabel} ${index + 1}`;
}

type ParagraphArrayFieldProps = {
  editable: boolean;
  field: ArrayFieldConfig;
  componentIndex: number;
  parentPath: Path;
  previewComponents: All8PageContent;
  updateComponentValue: (index: number, path: Path, value: unknown) => void;
  value: unknown;
};

/** Paragraph list field with rich editing, reordering and bulk editing. */
export const ParagraphArrayField: React.FC<ParagraphArrayFieldProps> = ({
  editable,
  field,
  componentIndex,
  parentPath: fullPath,
  previewComponents,
  updateComponentValue,
  value,
}) => {
  const items: string[] = Array.isArray(value) ? value.map((x) => (typeof x === "string" ? x : String(x ?? ""))) : [];
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [editModalIndex, setEditModalIndex] = useState<number | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const itemLabel = field.itemLabel ?? "Paragraph";

  const handleSaveSingle = (html: string) => {
    if (editModalIndex === null) return;
    const next = [...items];
    next[editModalIndex] = html;
    updateComponentValue(componentIndex, fullPath, next);
    setEditModalIndex(null);
  };

  const handleSaveBulk = (next: string[]) => {
    updateComponentValue(componentIndex, fullPath, next);
    setBulkOpen(false);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">{field.label}</span>
        <div className="flex flex-wrap items-center gap-2">
          {editable && (
            <>
              <button
                type="button"
                onClick={() => setBulkOpen(true)}
                className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
              >
                Raw
              </button>
              <button
                type="button"
                onClick={() => {
                  updateComponentValue(componentIndex, fullPath, [...items, ""]);
                  setExpandedIndex(items.length);
                  setEditModalIndex(items.length);
                }}
                className="shrink-0 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
              >
                Add
              </button>
            </>
          )}
        </div>
      </div>
      <p className="text-[11px] text-slate-500">{items.length} {itemLabel.toLowerCase()}{(items.length !== 1) ? "s" : ""}</p>

      <div className="space-y-2">
        {items.length === 0 && (
          <p className="py-2 text-xs text-slate-500">No paragraphs yet. Click Add or use “Raw” to edit all at once (separate with blank lines).</p>
        )}
        {items.map((item, idx) => (
          <div
            key={`${field.label}-${idx}`}
            className="overflow-hidden rounded-xl border border-slate-200 bg-white"
          >
            <div
              role="button"
              tabIndex={0}
              onClick={() => setExpandedIndex((i) => (i === idx ? null : idx))}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setExpandedIndex((i) => (i === idx ? null : idx))}
              className="flex min-w-0 items-center gap-2 px-3 py-2 text-left transition hover:bg-slate-50"
              aria-expanded={expandedIndex === idx}
            >
              {expandedIndex === idx ? (
                <FiChevronDown className="h-4 w-4 shrink-0 text-slate-500" />
              ) : (
                <FiChevronRight className="h-4 w-4 shrink-0 text-slate-500" />
              )}
              <span className="min-w-0 flex-1 truncate text-sm text-slate-700">
                {summary(item, idx, itemLabel)}
              </span>
            </div>

            {expandedIndex === idx && (
              <div className="space-y-2 border-t border-slate-100 bg-slate-50/50 p-3">
                {editable ? (
                  <ParagraphRichEditor
                    value={item}
                    onChange={(html) => {
                      const next = [...items];
                      next[idx] = html;
                      updateComponentValue(componentIndex, fullPath, next);
                    }}
                    minHeight="80px"
                    placeholder={`${itemLabel} ${idx + 1}…`}
                    className="rounded-lg border border-slate-200 bg-white"
                  />
                ) : (
                  <div
                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 [&_strong]:font-semibold [&_strong]:text-[#005CA9] [&_b]:font-semibold [&_b]:text-[#005CA9]"
                    dangerouslySetInnerHTML={{ __html: sanitizeParagraphHtml(item) }}
                  />
                )}
                {editable && (
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <button
                      type="button"
                      onClick={() => {
                        updateComponentValue(componentIndex, fullPath, items.filter((_, i) => i !== idx));
                        setExpandedIndex(null);
                      }}
                      className="rounded-full border border-rose-200 px-2.5 py-1 font-semibold text-rose-600 transition hover:border-rose-300 hover:bg-rose-50"
                    >
                      Remove
                    </button>
                    <button
                      type="button"
                      disabled={idx === 0}
                      onClick={() =>
                        updateComponentValue(componentIndex, fullPath, reorderList(items, idx, Math.max(0, idx - 1)))
                      }
                      className="rounded-full border border-slate-200 px-2.5 py-1 font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Move up
                    </button>
                    <button
                      type="button"
                      disabled={idx === items.length - 1}
                      onClick={() =>
                        updateComponentValue(componentIndex, fullPath, reorderList(items, idx, Math.min(items.length - 1, idx + 1)))
                      }
                      className="rounded-full border border-slate-200 px-2.5 py-1 font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Move down
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <ParagraphRichEditorModal
        show={editModalIndex !== null}
        value={editModalIndex !== null ? items[editModalIndex] ?? "" : ""}
        onSave={handleSaveSingle}
        onCancel={() => setEditModalIndex(null)}
        title={editModalIndex !== null ? `Edit ${itemLabel} ${editModalIndex + 1}` : "Edit paragraph"}
      />
      <ParagraphBulkEditorModal
        show={bulkOpen}
        paragraphs={items}
        onSave={handleSaveBulk}
        onCancel={() => setBulkOpen(false)}
        title="Raw"
      />
    </div>
  );
};
